import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';

// ─── Loading spinner ──────────────────────────────────────────────────────────
export function Spinner({ text = 'Loading…' }) {
  return (
    <div className="loading">
      <div className="spinner" />
      <span>{text}</span>
    </div>
  );
}

// ─── Error banner ─────────────────────────────────────────────────────────────
export function ErrorBanner({ message, onRetry }) {
  if (!message) return null;
  return (
    <div className="alert alert-danger" style={{ marginBottom: 16 }}>
      <i className="ti ti-alert-circle" />
      <div>
        {message}
        {onRetry && <button className="btn btn-sm" style={{ marginLeft: 10 }} onClick={onRetry}>Retry</button>}
      </div>
    </div>
  );
}

// ─── Status badge ─────────────────────────────────────────────────────────────
export function Badge({ status, label }) {
  const map = {
    ok:'badge-ok', good:'badge-ok', fulfilled:'badge-ok', complete:'badge-ok',
    received:'badge-ok', active:'badge-ok', success:'badge-ok', processed:'badge-ok',
    low:'badge-amber', warning:'badge-amber', pending:'badge-amber', partial:'badge-amber',
    in_progress:'badge-blue', sent:'badge-blue', planned:'badge-blue', processing:'badge-blue',
    pending_approval:'badge-amber', open:'badge-amber',
    critical:'badge-crit', backordered:'badge-crit',
    cancelled:'badge-gray', rejected:'badge-gray', draft:'badge-gray', error:'badge-crit',
  };
  const cls = map[status?.toLowerCase()] || 'badge-gray';
  return <span className={`badge ${cls}`}>{label || status}</span>;
}

// ─── Modal ────────────────────────────────────────────────────────────────────
export function Modal({ title, onClose, children, footer, wide, extraWide }) {
  // No backdrop click or Escape key close — require explicit save/cancel action
  return (
    <div className="modal-overlay">
      <div className="modal" style={extraWide ? { maxWidth: 1100 } : wide ? { maxWidth: 720 } : {}}>
        <div className="modal-header">
          <span className="modal-title">{title}</span>
          <button className="btn btn-icon" onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>
  );
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────
export function ConfirmDialog({ message, onConfirm, onCancel, danger }) {
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-body" style={{ padding: 24 }}>
          <p style={{ marginBottom: 20 }}>{message}</p>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={onCancel}>Cancel</button>
            <button className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Import button + file picker ──────────────────────────────────────────────
export function ImportButton({ onFile, accept = '.csv,.xlsx', label = 'Import' }) {
  const id = `file-${Math.random().toString(36).slice(2)}`;
  return (
    <>
      <label htmlFor={id} className="btn" style={{ cursor: 'pointer' }}>
        <i className="ti ti-upload" /> {label}
      </label>
      <input id={id} type="file" accept={accept} style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files[0]) onFile(e.target.files[0]); e.target.value = ''; }} />
    </>
  );
}

// ─── Import result summary ────────────────────────────────────────────────────
export function ImportResult({ result }) {
  if (!result) return null;
  const hasErrors = result.errors?.length > 0;
  const hasFuzzy = result.fuzzyMatched?.length > 0;
  const cls = (hasErrors || hasFuzzy) ? 'alert-warning' : 'alert-success';
  return (
    <div className={`alert ${cls}`} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
      <strong>Import complete:</strong> {result.created ?? 0} created, {result.updated ?? 0} updated
      {hasFuzzy && (
        <div style={{ marginTop: 8, width: '100%' }}>
          <div style={{ fontSize: 12, fontWeight: 600 }}>
            {result.fuzzyMatched.length} new product{result.fuzzyMatched.length !== 1 ? 's' : ''} created with a name that closely matches an existing product — possible duplicate, please review:
          </div>
          <ul style={{ marginTop: 4, paddingLeft: 16 }}>
            {result.fuzzyMatched.slice(0, 5).map((f, i) => (
              <li key={i} style={{ fontSize: 12 }}>
                Row {f.row}: created SKU <strong>{f.fileSku}</strong> "{f.fileName}" — looks similar to existing SKU <strong>{f.existingSku}</strong> "{f.existingName}"
              </li>
            ))}
            {result.fuzzyMatched.length > 5 && <li style={{ fontSize: 12 }}>…and {result.fuzzyMatched.length - 5} more</li>}
          </ul>
        </div>
      )}
      {hasErrors && (
        <ul style={{ marginTop: 6, paddingLeft: 16 }}>
          {result.errors.slice(0, 5).map((e, i) => (
            <li key={i} style={{ fontSize: 12 }}>Row {e.row}: {e.error}</li>
          ))}
          {result.errors.length > 5 && <li style={{ fontSize: 12 }}>…and {result.errors.length - 5} more</li>}
        </ul>
      )}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────
export function EmptyState({ icon = 'ti-inbox', message = 'No records found' }) {
  return (
    <div className="empty-state">
      <i className={`ti ${icon}`} />
      <p>{message}</p>
    </div>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────
export function Pagination({ page, total, perPage = 50, onChange }) {
  const pages = Math.ceil(total / perPage);
  if (pages <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 16 }}>
      <button className="btn btn-sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
        <i className="ti ti-chevron-left" />
      </button>
      <span style={{ padding: '4px 10px', fontSize: 13 }}>
        Page {page} of {pages}
      </span>
      <button className="btn btn-sm" disabled={page >= pages} onClick={() => onChange(page + 1)}>
        <i className="ti ti-chevron-right" />
      </button>
    </div>
  );
}


// ─── Toast notification ───────────────────────────────────────────────────────
// Uses direct DOM manipulation so toasts survive React re-renders and component unmounts.
let _toastEl = null;
function getToastRoot() {
  if (!_toastEl) {
    _toastEl = document.createElement('div');
    _toastEl.id = 'sf-toast-root';
    document.body.appendChild(_toastEl);
  }
  return _toastEl;
}

export function showGlobalToast(msg, type = 'success') {
  const root = getToastRoot();
  const div = document.createElement('div');
  div.className = 'alert alert-' + type;
  div.style.cssText = 'position:fixed;top:24px;left:24px;z-index:9999;min-width:280px;max-width:420px;box-shadow:0 4px 12px rgba(0,0,0,0.15);animation:fadeIn 0.2s ease;cursor:pointer;display:flex;align-items:center;gap:8px;';
  const icon = document.createElement('i');
  icon.className = 'ti ' + (type === 'success' ? 'ti-check' : 'ti-alert-circle');
  const text = document.createTextNode(msg);
  div.appendChild(icon);
  div.appendChild(text);
  div.onclick = () => div.remove();
  root.appendChild(div);
  setTimeout(() => { if (div.parentNode) div.remove(); }, 3500);
}

export function useToast() {
  const showToast = useCallback((msg, type = 'success') => {
    showGlobalToast(msg, type);
  }, []);
  return { showToast, Toast: null };
}

// ─── SearchableSelect ─────────────────────────────────────────────────────────
// A single-value select with inline search. Uses position:fixed on the dropdown
// so it escapes modal overflow clipping without needing a portal.
// Options: [{ value, label }]
export function SearchableSelect({ value, onChange, options, placeholder = '— Select —' }) {
  const [query, setQuery] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [dropPos, setDropPos] = React.useState({ top: 0, left: 0, width: 0 });
  const ref = React.useRef(null);
  const inputRef = React.useRef(null);

  const selected = options.find(o => String(o.value) === String(value));

  const openDropdown = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    }
    setQuery('');
    setOpen(true);
  };

  React.useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(o =>
    !query || o.label.toLowerCase().includes(query.toLowerCase())
  );

  const select = (opt) => { onChange(opt.value); setQuery(''); setOpen(false); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <input
        ref={inputRef}
        type="text"
        value={open ? query : (selected ? selected.label : '')}
        placeholder={placeholder}
        onClick={openDropdown}
        onChange={e => { setQuery(e.target.value); if (!open) openDropdown(); }}
        style={{
          width: '100%', boxSizing: 'border-box',
          border: '1px solid var(--border)', borderRadius: 6,
          padding: '7px 10px', fontSize: 13,
          background: 'var(--bg-primary)', color: 'var(--text-primary)',
          cursor: 'default', outline: 'none',
        }}
      />
      {open && (
        <div style={{
          position: 'fixed',
          zIndex: 99999,
          top: dropPos.top,
          left: dropPos.left,
          width: dropPos.width,
          background: 'var(--bg-primary, #ffffff)',
          border: '1px solid var(--border, #e2e8f0)',
          borderRadius: 6,
          boxShadow: '0 4px 20px rgba(0,0,0,0.18)',
          maxHeight: 240,
          overflowY: 'auto',
        }}>
          {filtered.length === 0 && (
            <div style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-tertiary)' }}>No results</div>
          )}
          {filtered.map(opt => (
            <div key={opt.value} onMouseDown={() => select(opt)}
              style={{
                padding: '7px 12px', fontSize: 13, cursor: 'pointer',
                backgroundColor: String(opt.value) === String(value) ? 'var(--bg-secondary, #f8fafc)' : 'transparent',
                borderBottom: '1px solid var(--border, #e2e8f0)',
              }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = 'var(--bg-secondary, #f8fafc)'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = String(opt.value) === String(value) ? 'var(--bg-secondary, #f8fafc)' : 'transparent'}
            >
              {opt.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
